import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { OrdersBaseComponent } from "@mypxplat/xplat/features";
import { BaseComponent, EventBusService, StorageService, UserService, OrderService, ProductService, FenderShopifyOrder } from "@mypxplat/xplat/core";
import { Router } from "@angular/router";
import { AppService } from "@mypxplat/xplat/web/core";
import { TranslateService } from "@ngx-translate/core";
import { EditAddressComponent } from "../modals";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-shared-orders",
  templateUrl: "orders.component.html",
  standalone: false,
})
export class OrdersComponent extends OrdersBaseComponent implements OnInit, OnDestroy {
  public destroy$: Subject<any> = new Subject();
  public orders: Array<any>;
  public fenderOrders: Array<FenderShopifyOrder>;
  public recurringPayments: Array<any>;
  public loading: boolean = true;
  public displayedSection: string = "orders";
  public orderMetadata: any = {};
  public addresses: Array<any>;
  public editingAddresses: any = {};
  public giftCards: Array<any>;
  constructor(
    storageService: StorageService,
    userService: UserService,
    public orderService: OrderService,
    public productService: ProductService,
    private _router: Router,
    public appService: AppService,
    private translate: TranslateService,
    private eventBusService: EventBusService
  ) {
    super(userService);
  }

  getAddressMetadata(address: any) {
    return [
      {
        title: this.translate.instant("orders.phone_label"),
        value: address.phone,
      },
      {
        title: address.default ? this.translate.instant("orders.default_label") : "",
        value: "",
      },
    ];
  }

  ngOnInit() {
    window.scrollTo(0, 0);

    this.orderService.getOrders().subscribe((result: any[]) => {
      this.orders = result.filter((order) => order.type != "recurring");
      this.recurringPayments = result.filter((order) => order.type == "recurring");
      this.loading = false;
    });
    this.orderService.getFenderShopifyAccountDetails(this.userService.user.email, true).subscribe((result: any) => {
      let defaultAddressId = this.orderService.fenderUserShopData?.defaultAddress?.id;
      this.addresses = this.orderService.fenderUserShopData?.addresses;
      if (this.addresses?.length && defaultAddressId) {
        this.addresses.forEach((address) => {
          if (address.id == defaultAddressId) address.default = true;
          // Calcular metadata para cada dirección
          address.metadata = this.getAddressMetadata(address);
        });
      } else if (this.addresses?.length) {
        this.addresses.forEach((address) => {
          address.metadata = this.getAddressMetadata(address);
        });
      }
      if (result?.shopifyId) {
        setTimeout(() => {
          this.orderService.getFenderShopifyOrders(result.shopifyId, result.storeId).subscribe((orders: any) => {
            orders.forEach((order) => {
              this.orderMetadata[order.id] = [
                { title: this.translate.instant("orders.ordered_label"), value: new Date(order.createdAt).toLocaleDateString() },
                { title: this.translate.instant("orders.total"), value: order.totalPrice.amount + " " + order.totalPrice.currency },
              ];
              if (order.fulfillments.length) {
                this.orderMetadata[order.id].push({ title: this.translate.instant("orders.status"), value: this.translate.instant("orders.shipped_status") });
              }
            });
            this.fenderOrders = orders;
            setTimeout(() => {
              this.orderService.getFenderGiftCards(result.shopifyId, result.storeId).subscribe((giftCards: any) => {
                this.giftCards = giftCards.map((card: any) => ({
                  ...card,
                  metadata: [
                    {
                      title: this.translate.instant("orders.expires_label"),
                      value: card.expires,
                    },
                    {
                      title: this.translate.instant("orders.balance_label"),
                      value: card.balance,
                    },
                  ],
                }));
              });
            }, 100);
          });
        }, 100);
      }
    });
  }

  switchSection(section, force?) {
    if (this.userService.appId === "fender") {
      const sectionLabels: Record<string, string> = {
        orders: "My Orders",
        addresses: "Address Book",
        gift_cards: "Gift Cards",
      };
      const pageName = sectionLabels[section] || section;
      const navigationLabel = sectionLabels[section] || section;
      this.eventBusService.emit(this.eventBusService.types.subNavigationChanged, {
        pageName,
        navigationLabel,
      });
    }
    this.displayedSection = section;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  deleteAddress(address: any) {
    if (confirm(this.translate.instant("orders.confirm_delete_address"))) {
      this.editingAddresses[address.id] = true;
      let addressId = address.id.split("MailingAddress/")[1].split("?model_name=CustomerAddress")[0];
      this.orderService.deleteShopifyAddress(this.orderService.fenderUserShopData.shopifyId, addressId).subscribe((result: any) => {
        this.addresses = this.orderService.fenderUserShopData?.addresses;
        let defaultAddressId = this.orderService.fenderUserShopData?.defaultAddress?.id;
        if (this.addresses?.length) {
          this.addresses.forEach((address) => {
            if (address.id == defaultAddressId) address.default = true;
            address.metadata = this.getAddressMetadata(address);
          });
        }
        this.editingAddresses[address.id] = false;
      });
    }
  }
  editAddress(address: any) {
    const modalRef = this.appService.showModal(EditAddressComponent, { size: "sm" });
    let transformedAddress = {
      city: address.city,
      countryCode: address.countryCode,
      id: address.id,
      address_phone: address.phone,
      state: address.provinceCode,
      street: address.address1,
      street2: address.address2,
      zipcode: address.zip,
    };
    modalRef.componentInstance.address = transformedAddress;
    modalRef.componentInstance.fenderShopifyAddress = true;
    modalRef.componentInstance.addressAdded.subscribe((result) => {
      if (result) {
        // find the ID in a string that looks like this: gid://shopify/MailingAddress/11046665912615?model_name=CustomerAddress
        this.editingAddresses[address.id] = true;
        let addressId = result.id.split("MailingAddress/")[1].split("?model_name=CustomerAddress")[0];
        this.orderService
          .addUpdateShopifyAddress(
            this.orderService.fenderUserShopData.shopifyId,
            {
              address1: result.street,
              address2: result.street2,
              city: result.city,
              company: "",
              provinceCode: result.state,
              countryCode: result.countryCode,
              firstName: this.userService.user.firstName,
              lastName: this.userService.user.lastName,
              phone: result.address_phone,
              zip: result.zipcode,
            },
            addressId
          )
          .subscribe((result) => {
            this.addresses = this.orderService.fenderUserShopData?.addresses;
            let defaultAddressId = this.orderService.fenderUserShopData?.defaultAddress?.id;
            if (this.addresses?.length) {
              this.addresses.forEach((address) => {
                if (address.id == defaultAddressId) address.default = true;
                address.metadata = this.getAddressMetadata(address);
              });
            }
            this.editingAddresses[address.id] = false;
          });

        // heres where we actually save the address. coming soon.
      }
    });
  }

  addAddress() {
    const modalRef = this.appService.showModal(EditAddressComponent, { size: "sm" });
    modalRef.componentInstance.address = {};
    modalRef.componentInstance.fenderShopifyAddress = true;
    modalRef.componentInstance.addressAdded.subscribe((result) => {
      if (result) {
        this.orderService
          .addUpdateShopifyAddress(this.orderService.fenderUserShopData.shopifyId, {
            address1: result.street,
            address2: result.street2,
            city: result.city,
            company: "",
            provinceCode: result.state,
            countryCode: result.countryCode,
            firstName: this.userService.user.firstName,
            lastName: this.userService.user.lastName,
            phone: result.address_phone,
            zip: result.zipcode,
          })
          .subscribe((result) => {
            this.addresses = this.orderService.fenderUserShopData?.addresses;
            let defaultAddressId = this.orderService.fenderUserShopData?.defaultAddress?.id;
            if (this.addresses?.length && defaultAddressId) {
              this.addresses.forEach((address) => {
                if (address.id == defaultAddressId) address.default = true;
                address.metadata = this.getAddressMetadata(address);
              });
            } else if (this.addresses?.length) {
              this.addresses.forEach((address) => {
                address.metadata = this.getAddressMetadata(address);
              });
            }
          });

        // heres where we actually save the address. coming soon.
      }
    });
  }

  goShopping() {
    window.open("https://presonus.com");
  }

  goToProduct(id) {
    this._router.navigate(["/products/detail", id]);
  }
}
